<template>
    <section v-if="maintainer.serviceLevelAgreements?.length > 0" :class="{ 'mt-4': !collapsed }" class="sla">
        <div :class="{ collapsed }" class="grid">
            <div v-if="!isTablet" class="grid-header">
                <div class="grid-cell">
                    {{ trans('maintainers.overview.sla.asset_type.title') }}
                </div>
                <div class="grid-cell">
                    {{ trans('maintainers.overview.sla.contracting_authority.title') }}
                </div>
                <div class="grid-cell">
                    {{ trans('maintainers.sla.agreement_term.title') }}
                </div>
                <div class="grid-cell">
                    {{ trans('maintainers.overview.support_contacts.title') }}
                </div>
                <div class="grid-cell"></div>
            </div>
            <div
                v-for="sla in maintainer.serviceLevelAgreements"
                :key="sla.id"
                :class="{ highlight: shouldHighlightSla(sla) }"
                class="grid-row-wrapper"
            >
                <div :id="sla.id" class="grid-cell max-md:mt-4">
                    <div class="h6">{{ sla.type.shortLabel }}</div>
                    <div class="whitespace-nowrap">
                        {{
                            trans('stakeholders.overview.assets_count.label', {
                                count: sla.affectedAssetsCount || 0,
                            })
                        }}
                    </div>
                    <div class="whitespace-nowrap">
                        {{
                            trans('stakeholders.overview.locations_count.label', {
                                count: sla.affectedTopLevelLocationsCount || 0,
                            })
                        }}
                    </div>
                    <hr class="mb-0 md:hidden" />
                </div>
                <div class="grid-cell max-md:pt-0 max-md:pb-1">
                    <div class="h6 mb-1 md:hidden">
                        {{ trans('maintainers.overview.sla.contracting_authority.title') }}
                    </div>
                    {{ sla.contractingAuthority?.name || '-' }}
                </div>
                <div class="grid-cell max-md:py-0 max-md:pt-2">
                    <div class="h6 mb-1 md:hidden">
                        {{ trans('maintainers.sla.agreement_term.title') }}
                    </div>
                    <div :class="{ 'text-red-500': getExpirationInfo(sla) }" v-html="getExpirationDateRange(sla)" />
                    <div v-if="getExpirationInfo(sla)" class="flex items-center gap-1 text-[13px] whitespace-nowrap text-red-500">
                        <exclamation-mark type="filled" />
                        {{ getExpirationInfo(sla) }}
                    </div>
                    <hr class="mb-0 md:hidden" />
                </div>
                <div class="grid-cell">
                    <div class="h6 mb-1 md:hidden">
                        {{ trans('maintainers.overview.support_contacts.title') }}
                    </div>
                    <stakeholder-contact-list :contacts="getScheduleContacts(sla)" />
                </div>
                <div v-if="!isTablet && hasAnyPermission(['maintainer.update'])" class="grid-cell grid-action-cell flex justify-end gap-1">
                    <modal-link
                        :href="route('maintainers.slas.edit', { maintainer, sla })"
                        :tooltip="trans('general.action.edit')"
                        icon="cil-pencil"
                    />
                    <jet-button
                        v-c-tooltip="{
                            content: trans('general.action.delete'),
                        }"
                        :destructive="true"
                        :is-processing="isDeleting(sla)"
                        class="btn-icon"
                        type="button"
                        variant="secondary"
                        @click.stop="deleteSla(sla)"
                    >
                        <icon name="cil-trash" />
                    </jet-button>
                </div>
                <div v-else class="grid-cell" />
            </div>
        </div>
    </section>
</template>
<script setup>
import ExclamationMark from '@/../icons/ExclamationMark.vue';
import JetButton from '@/Jetstream/Button.vue';
import { isTablet } from '@/Utils/UseMedia';
import { DateTime } from 'luxon';
import { trans } from '@/mixins/i18n.js';
import fullDate from '@/filters/fullDate';
import { router, usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import ModalLink from '@/Components/ModalLink.vue';
import StakeholderContactList from '@/Pages/Stakeholders/Components/StakeholderContactList.vue';
import { ref } from 'vue';

const props = defineProps({
    maintainer: Object,
    collapsed: Boolean,
});

const page = usePage();
const deletingSlas = ref([]);

const getScheduleContacts = (sla) => {
    if (!sla.supportSchedules?.length || !props.maintainer.supportContacts?.length) {
        return [];
    }

    const channelTypesByContactId = new Map();
    sla.supportSchedules.forEach((schedule) => {
        schedule.contacts?.forEach((selection) => {
            const existing = channelTypesByContactId.get(selection.contactId) ?? new Set();
            (selection.channelTypes ?? []).forEach((type) => existing.add(type));
            channelTypesByContactId.set(selection.contactId, existing);
        });
    });

    return props.maintainer.supportContacts
        .filter((contact) => channelTypesByContactId.has(contact.id))
        .map((contact) => ({
            ...contact,
            channelTypes: Array.from(channelTypesByContactId.get(contact.id)),
        }));
};

const getExpirationDateRange = (sla) => {
    if (!sla || (!sla.startDate && !sla.endDate)) {
        return '';
    }

    const startDate = DateTime.fromFormat(sla.startDate, 'y-MM-dd');
    const endDate = DateTime.fromFormat(sla.endDate, 'y-MM-dd');
    const locale = page.props.locale;

    return [fullDate(startDate, locale), fullDate(endDate, locale)].map((el) => `<span class="whitespace-nowrap">${el}</span>`).join(' - ');
};

const getExpirationInfo = (sla) => {
    if (!sla || !sla.startDate || !sla.endDate) {
        return trans('maintainers.overview.sla_missing');
    }
    if (sla.expired) {
        return trans('maintainers.overview.sla_expired');
    }

    if (sla.expiresSoon) {
        return trans('maintainers.overview.sla_expires_soon');
    }
};

const isDeleting = (sla) => deletingSlas.value.includes(sla.id);

const deleteSla = (sla) => {
    deletingSlas.value.push(sla.id);

    router.delete(
        route('maintainers.slas.destroy', {
            maintainer: props.maintainer,
            sla: sla.id,
        }),
        {
            preserveScroll: true,
            onFinish: () => (deletingSlas.value = deletingSlas.value.filter((id) => id !== sla.id)),
        },
    );
};

const shouldHighlightSla = (sla) => location.hash.includes(sla.id);
</script>
<style scoped>
.grid.collapsed {
    height: 0;
}
</style>
